import { UserProjectVO, DomainGroup, PageItem, PageStats } from '@/types/dashboard';
import { BriefSection } from '@/types/creation';
import { normalizeLegacyPlatformDomain, normalizeLegacyPlatformUrl } from '@/lib/platformDomain';
import { buildPublishedPageUrl, getPublishedPageDomain } from '@/lib/publishedPageUrl';

const EMPTY_STATS: PageStats = {
  conversion: '-',
  sessions: '-',
  leads: '-',
  delta: {
    conversion: '',
    sessions: '',
    leads: '',
    isUp: { conversion: false, sessions: false, leads: false },
  },
};

function toPageItem(project: UserProjectVO): PageItem {
  const sp = project.sitePaths?.[0];
  const effectiveProjectId = project.projectId ?? project.id;
  const rawPath = sp?.path ?? '/';
  const normalizedAccessUrl = project.accessUrl
    ? normalizeLegacyPlatformUrl(project.accessUrl.replace(/^(https?:\/\/)+/, 'https://'))
    : '';
  const url =
    buildPublishedPageUrl({
      sitePath: sp,
      path: rawPath,
      fallbackUrl: normalizedAccessUrl,
    }) || null;

  return {
    id: String(project.id),
    projectId: String(effectiveProjectId),
    projectTypeId: project.projectTypeId,
    tenantId: project.tenantId,
    projectDescription: project.projectDescription || '',
    siteId: sp ? String(sp.siteId) : undefined,
    instanceToken: project.instanceToken || undefined,
    title: project.projectName,
    url: url ?? undefined,
    // publishStatus 0=未发布→draft，1=已发布+status 1=可用→live，否则→attn
    status: project.publishStatus !== 1 ? 'draft' : project.status === 1 ? 'live' : 'attn',
    autopilot: project.publishStatus === 1,
    thumbnailColor: '#EDEEF8',
    thumbnailUrl: project.projectLogo || undefined,
    sitePaths: project.sitePaths?.length ? project.sitePaths : undefined,
    stats: EMPTY_STATS,
  };
}

/**
 * 将后端 UserProjectVO[] 转换为 Dashboard UI 所需的分组数据
 * - publishStatus === 1 的项目按第一个 sitePath 的 domain 分组
 * - publishStatus !== 1 的项目归入 drafts
 */
export function transformProjects(projects: UserProjectVO[]): {
  domainGroups: DomainGroup[];
  drafts: PageItem[];
} {
  // 后端偶发返回重复的 userProject（相同 id），会导致 React 列表 key 冲突
  // 这里按 id 去重：优先保留 publishStatus=1 的记录，其次保留后出现的记录
  const uniqueProjects: UserProjectVO[] = (() => {
    const map = new Map<number, UserProjectVO>();
    for (const p of projects) {
      const prev = map.get(p.id);
      if (!prev) {
        map.set(p.id, p);
        continue;
      }
      if (prev.publishStatus !== 1 && p.publishStatus === 1) {
        map.set(p.id, p);
        continue;
      }
      map.set(p.id, p);
    }
    return Array.from(map.values());
  })();

  const domainMap = new Map<string, PageItem[]>();
  const drafts: PageItem[] = [];

  for (const project of uniqueProjects) {
    const page = toPageItem(project);
    if (project.publishStatus !== 1) {
      drafts.push(page);
    } else {
      const sp0 = project.sitePaths?.[0];
      let domain =
        getPublishedPageDomain({
          sitePath: sp0,
          includeSiteKeyFallback: false,
        }) || 'unknown';
      if (domain === 'unknown' && page.url) {
        try {
          domain = normalizeLegacyPlatformDomain(new URL(page.url).host) || 'unknown';
        } catch {
          domain = 'unknown';
        }
      }
      if (!domainMap.has(domain)) domainMap.set(domain, []);
      domainMap.get(domain)!.push(page);
    }
  }

  const domainGroups: DomainGroup[] = [];
  domainMap.forEach((pages, domain) => {
    // 同域名内按顺序分配排名
    pages.forEach((p, i) => {
      p.rank = `#${i + 1}`;
    });
    domainGroups.push({ domain, pages });
  });

  return { domainGroups, drafts };
}

/**
 * 将 AI 返回的 Markdown 简报解析为 BriefSection[]
 *
 * 格式说明：
 * # 标题
 * 内容（如果是文本类型）
 *
 * # 标题
 * - [checked] 选项 A
 * - 选项 B
 */
export function parseMarkdownToBriefSections(markdown: string): BriefSection[] {
  if (!markdown) return [];

  const sections: BriefSection[] = [];
  const partRegex = /^#+\s+(.+)$/gm;
  let match;
  const indices: { start: number; length: number; heading: string }[] = [];

  while ((match = partRegex.exec(markdown)) !== null) {
    indices.push({
      start: match.index,
      length: match[0].length,
      heading: match[1].trim(),
    });
  }

  for (let i = 0; i < indices.length; i++) {
    const current = indices[i];
    const nextStart = indices[i + 1]?.start ?? markdown.length;
    const rawContent = markdown.substring(current.start + current.length, nextStart);
    const trimmedContent = rawContent.trim();
    // 允许内容为空，实现标题先出现，内容随后“打字机”输出的效果
    // 如果不是最后一个且为空，为了避免空洞，可以保留。不过由于是生成器，当前在生成的可能为空

    // 识别列表项的正则表达式
    const listRegex = /^([-*+]|\d+\.)\s+/;
    const lines = trimmedContent.split(/\r?\n/);

    // 判断是否为列表型章节：如果超过 50% 的非空行是列表项，则视为 select 类型
    const nonEmptyLines = lines.filter((l) => l.trim().length > 0);
    const listItemCount = nonEmptyLines.filter((l) => listRegex.test(l.trim())).length;
    const isListType = listItemCount > 0 && listItemCount >= nonEmptyLines.length * 0.5;

    if (isListType) {
      const items: { text: string; checked: boolean }[] = [];
      let currentItem: { text: string; checked: boolean } | null = null;

      lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        if (listRegex.test(trimmedLine)) {
          // 发现新列表项
          const isChecked = /\[(checked|x|X|v|√)\]/i.test(trimmedLine);
          const text = trimmedLine
            .replace(listRegex, '')
            .replace(/^\[(checked|x|X|v|√|\s)\]\s*/i, '')
            .trim();

          currentItem = { text, checked: isChecked };
          items.push(currentItem);
        } else if (currentItem) {
          // 属于上一个列表项的延续行
          currentItem.text += ' ' + trimmedLine;
        }
      });

      if (items.length > 0) {
        // 确保至少有一个被选中
        const selected = items.find((it) => it.checked) || items[0];
        if (!items.some((it) => it.checked)) selected.checked = true;

        sections.push({
          heading: current.heading,
          type: 'select',
          value: selected.text,
          items,
        });
        continue;
      }
    }

    // 默认为文本类型，保留原始换行结构但去除首尾空白
    sections.push({
      heading: current.heading,
      type: 'text',
      value: trimmedContent,
    });
  }

  return sections;
}
